// Захватывает {{path}} с произвольным whitespace внутри скобок. Lazy quantifier
// + negated character class [^{}] исключают вложенные `{` или `}` (ReDoS safe).
const PLACEHOLDER_REGEX = /\{\{\s*([^{}]*?)\s*\}\}/g;

export interface PlaceholderMatch {
  start: number;
  end: number;
  raw: string;
}

export interface InvalidRange {
  start: number;
  end: number;
  message?: string;
  suggestion?: string;
}

export type RenderSegment =
  | { kind: "text"; text: string }
  | {
      kind: "chip";
      text: string;
      raw: string;
      invalid: boolean;
      start: number;
      end: number;
      backendMessage?: string;
    };

export const collectPlaceholders = (input: string): PlaceholderMatch[] => {
  const out: PlaceholderMatch[] = [];
  PLACEHOLDER_REGEX.lastIndex = 0;
  let match = PLACEHOLDER_REGEX.exec(input);
  while (match !== null) {
    out.push({
      start: match.index,
      end: match.index + match[0].length,
      raw: (match[1] ?? "").trim(),
    });
    match = PLACEHOLDER_REGEX.exec(input);
  }
  return out;
};

// Превращает строку шаблона + массив матчей в плоский список сегментов для
// рендера: чередующиеся куски текста и чипы-плейсхолдеры. Чип помечается
// invalid, если он либо в externally переданном invalidRanges (parse-ошибка
// или unknown-variable от бэка), либо его raw не проходит локальный whitelist.
export const buildSegments = (
  value: string,
  matches: PlaceholderMatch[],
  invalidRanges: InvalidRange[] | undefined,
  isValid: (path: string) => boolean,
): RenderSegment[] => {
  const segments: RenderSegment[] = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) {
      segments.push({ kind: "text", text: value.slice(cursor, m.start) });
    }
    const backend = invalidRanges?.find(
      (r) => m.start === r.start && m.end === r.end,
    );
    const invalid = !!backend || m.raw.length === 0 || !isValid(m.raw);
    segments.push({
      kind: "chip",
      text: value.slice(m.start, m.end),
      raw: m.raw,
      invalid,
      start: m.start,
      end: m.end,
      backendMessage: backend?.message,
    });
    cursor = m.end;
  }
  if (cursor < value.length) {
    segments.push({ kind: "text", text: value.slice(cursor) });
  }
  return segments;
};
